<template>
  <div class="vehicle-assignments">
    <div class="card">
      <div v-if="!hideHeader" class="card-header d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          <i class="fas fa-history"></i> {{ $t('Vehicle Assignments History') }}
        </h5>
        <b-button
            variant="primary"
            class="button-primary"
            size="sm"
            @click="handleAssignClick"
            :disabled="loading"
        >
          <i class="fas fa-exchange-alt"></i> {{ $t('Assign Vehicle') }}
        </b-button>
      </div>
 
      <div class="card-body">
        <div v-if="loading" class="text-center py-4">
          <i class="fas fa-spinner fa-spin fa-2x"></i>
          <p class="mt-2">{{ $t('Loading assignments') }}...</p>
        </div>
 
        <div v-else-if="assignments.length > 0" class="table-responsive">
          <table class="table table-striped table-hover">
            <thead>
            <tr>
              <th>{{ $t('Name') }}</th>
              <th>{{ $t('Period') }}</th>
              <th>{{ $t('Vehicle Type') }}</th>
<!--              <th>{{ $t('Telephone Pulsos') }}</th>-->
              <th>{{ $t('Center') }}</th>
              <th>{{ $t('Unit') }}</th>
              <th class="col-devices">{{ $t('Devices') }}</th>
              <th>{{ $t('Status') }}</th>
              <!-- <th>{{ $t('Carnaval')}}</th> -->
              <th>{{ $t('Created By') }}</th>
              <th>{{ $t('Actions') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="assignment in sortedAssignments" :key="assignment.id">
              <td>{{ formatPhone(assignment.name) }}</td>
              <td>
                <div>
                  <strong>{{ $t('Start') }}:</strong> {{ formatDate(assignment.start_date) }}
                </div>
                <div v-if="assignment.end_date">
                  <strong>{{ $t('End') }}:</strong> {{ formatDate(assignment.end_date) }}
                </div>
                <div v-else class="text-success">
                  <strong>{{ $t('Active') }}</strong>
                </div>
              </td>
              <td>
                {{ assignment.vehicle_type ? assignment.vehicle_type.sigla : '-' }}
              </td>
<!--              <td>{{ formatPhone(assignment.telephone_pulsos) }}</td>-->
              <td>{{ assignment.center ? assignment.center.name : '-' }}</td>
              <td>{{ assignment.unit ? assignment.unit.name : '-' }}</td>
              <td>
                <div v-if="assignment.devices && assignment.devices.length">
                  <span
                      v-for="d in assignment.devices"
                      :key="d.id"
                      class="badge mr-1"
                      :class="isPrimaryDevice(d) ? 'badge-primary' : 'badge-secondary'"
                  >
                    {{ formatDeviceLabel(d) }}<span v-if="isPrimaryDevice(d)"> · {{ $t('Primary') }}</span>
                  </span>
                </div>
                <div v-else class="text-muted">-</div>
              </td>
              <td>
                  <span :class="getStatusClass(assignment.status)">
                    {{ getStatusLabel(assignment.status) }}
                  </span>
              </td>
              <!-- <td>
                <span v-if="assignment.is_carnival === 1" class="badge badge-success">
                  {{ $t('Yes') }}
                </span>
                <span v-else-if="assignment.is_carnival === 0" class="badge badge-secondary">
                  {{ $t('No') }}
                </span>
                <span v-else class="text-muted">-</span>
              </td> -->
              <td>{{ assignment.created_by ? assignment.created_by.name : '-' }}</td>
              <td>
                <div class="btn-group" role="group">
                  <b-button
                      v-if="assignment.status === 'active'"
                      variant="danger"
                      class="button-danger"
                      size="sm"
                      @click="finishAssignment(assignment)"
                      :disabled="finishingId === assignment.id"
                  >
                    <i class="fas fa-stop-circle"></i>
                  </b-button>
 
                  <b-button
                      size="sm"
                      variant="primary"
                      class="button-primary btn-primary btn-sm"
                      @click="showDetails(assignment)"
                  >
                    <i class="fas fa-eye"></i>
                  </b-button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
 
        <div v-else class="text-center py-4">
          <i class="fas fa-inbox fa-3x text-muted"></i>
          <p class="mt-3 text-muted">{{ $t('No assignments found for this vehicle') }}</p>
        </div>
      </div>
    </div>
 
    <b-modal
        v-model="showTransferModal"
        :title="$t('Assign Vehicle')"
        size="lg"
        @hide="resetTransferForm"
        centered
        backdrop
    >
      <ValidationObserver ref="transferFormObserver" v-slot="{ validate }">
        <b-form @submit.prevent="handleTransfer(validate)">
          <div class="row">
            <div class="col-md-6">
              <ValidationProvider name="Name" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Assignment Name") }}:</label>
                <b-form-input
                    v-model="transferForm.name"
                    :placeholder="$t('Enter assignment name')"
                    :state="errors.length ? false : null"
                />
                <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
              </ValidationProvider>
            </div>
            <div class="col-md-6">
              <label>{{ $t("Identifier Code") }}:</label>
              <b-form-input
                  v-model="transferForm.identifier_code"
                  :placeholder="$t('Enter identifier code')"
                  maxlength="30"
              />
              <small class="text-muted d-block">
                {{ $t('Integration code used by the dispatch system. It follows the operational prefix, not the vehicle.') }}
              </small>
              <small v-if="lastIdentifierCode && !transferForm.identifier_code" class="text-warning d-block">
                {{ $t('Clearing this code will make the vehicle disappear from the dispatch map.') }}
              </small>
            </div>
            <div class="col-md-6">
              <ValidationProvider name="Vehicle Type" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Vehicle Type") }}:</label>
                <v-select
                    :options="vehicleTypes"
                    :reduce="type => type.id"
                    label="sigla"
                    v-model="transferForm.vehicle_type_id"
                    class="vselect"
                    :class="{ 'is-invalid': errors.length }"
                />
                <span class="invalid-feedback">{{ $t(errors[0]) }}</span>
              </ValidationProvider>
            </div>
 
<!--            <div class="col-md-6">-->
<!--              <ValidationProvider name="Telephone Pulsos" rules="required" v-slot="{ errors }">-->
<!--                <label><span class="required">*</span> {{ $t("Telephone Pulsos") }}:</label>-->
<!--                <b-form-input-->
<!--                    v-model="transferForm.telephone_pulsos"-->
<!--                    v-mask="'(##) #####-####'"-->
<!--                    placeholder="(00) 00000-0000"-->
<!--                    :state="errors.length ? false : null"-->
<!--                />-->
<!--                <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>-->
<!--              </ValidationProvider>-->
<!--            </div>-->
 
            <div class="col-md-6">
              <ValidationProvider name="Central" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Central") }}:</label>
                <v-select
                    :options="centers"
                    :reduce="center => center.id"
                    label="name"
                    v-model="transferForm.center_id"
                    class="vselect"
                  @input="onCenterChange"
                    :class="{ 'is-invalid': errors.length }"
                />
                <span class="invalid-feedback">{{ $t(errors[0]) }}</span>
              </ValidationProvider>
            </div>
 
            <div class="col-md-6">
              <ValidationProvider name="Unit" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Unit") }}:</label>
                <v-select
                    :options="transferUnits"
                    :reduce="unit => unit.id"
                    label="name"
                    v-model="transferForm.unit_id"
                    class="vselect"
                  :class="{ 'is-invalid': errors.length }"
                />
                <span class="invalid-feedback">{{ $t(errors[0]) }}</span>
              </ValidationProvider>
            </div>
 
            <div class="col-md-6">
              <label>{{ $t('Devices') }}:</label>
              <v-select
                  :options="devices"
                  :multiple="true"
                  :reduce="d => d.id"
                  label="display"
                  v-model="transferForm.device_ids"
                  class="vselect"
                  :disabled="devicesLoading || !transferForm.center_id"
                  :placeholder="getDevicesPlaceholder()"
                  @input="onTransferDeviceIdsChange"
              />
            </div>
 
            <div class="col-md-12" v-if="transferForm.device_ids.length > 1">
              <label class="d-block">{{ $t('Primary Device') }}</label>
              <small class="text-muted d-block mb-2">{{ $t('The primary device is the vehicle\'s official position. Other devices must stay within the radius of it.') }}</small>
              <div class="d-flex flex-wrap" style="gap: 0.75rem;">
                <b-form-radio
                    v-for="deviceId in transferForm.device_ids"
                    :key="deviceId"
                    v-model="transferForm.primary_device_id"
                    :value="deviceId"
                    name="primary_device_id"
                >
                  {{ deviceLabelById(deviceId) }}
                </b-form-radio>
              </div>
            </div>
 
            <div class="col-md-6" v-if="transferForm.device_ids.length > 1">
              <label>{{ $t('Detachment Radius (meters)') }}:</label>
              <b-form-input
                  type="number"
                  min="1"
                  v-model.number="transferForm.detachment_radius_meters"
                  :placeholder="$t('Leave blank to disable detachment detection')"
              />
            </div>
            <!-- <div class="form-group col-md-6">
              <label>{{ $t('Carnival') }}</label>
              <b-form-select
                v-model="transferForm.is_carnival"
                :options="[
                  {value: null, text: $t('Select')},
                  { value: 1, text: $t('Yes') },
                  { value: 0, text: $t('No') }
                ]"
              />
            </div> -->
            <div class="col-md-12">
              <ValidationProvider name="Start Date" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Start Date") }}:</label>
                <b-form-input
                    type="date"
                    v-model="transferForm.start_date"
                    :state="errors.length ? false : null"
                />
                <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
              </ValidationProvider>
            </div>
 
            <div class="col-md-12">
              <ValidationProvider name="Notes" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Motivo") }}:</label>
                <b-form-textarea
                    v-model="transferForm.notes"
                    rows="3"
                    :placeholder="$t('Motivo (obrigatório)')"
                    :state="errors.length ? false : null"
                />
                <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
              </ValidationProvider>
            </div>
          </div>
        </b-form>
      </ValidationObserver>
 
      <template #modal-footer>
        <b-button variant="secondary" @click="showTransferModal = false">
          {{ $t('Cancel') }}
        </b-button>
        <b-button
            variant="primary"
            class="button-primary"
            @click="handleTransfer()"
            :disabled="transferring"
        >
          <template v-if="transferring">
            <i class="fas fa-spinner fa-spin"></i> {{ $t('Assigning') }}...
          </template>
          <template v-else>
            <i class="fas fa-exchange-alt"></i> {{ $t('Assign') }}
          </template>
        </b-button>
      </template>
    </b-modal>
 
    <!-- Finish Modal -->
    <b-modal
        v-model="showFinishModal"
        :title="$t('Finish Assignment')"
        size="md"
        centered
        backdrop
    >
      <ValidationObserver ref="finishFormObserver" v-slot="{ validate }">
        <b-form @submit.prevent="handleFinish(validate)">
          <div class="row">
            <div class="col-md-12">
              <ValidationProvider name="End Date" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("End Date") }}:</label>
                <b-form-input
                    type="date"
                    v-model="finishForm.end_date"
                    :state="errors.length ? false : null"
                />
                <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
              </ValidationProvider>
            </div>
 
            <div class="col-md-12">
              <ValidationProvider name="Finish Reason" rules="required" v-slot="{ errors }">
                <label><span class="required">*</span> {{ $t("Finish Reason") }}:</label>
                <b-form-textarea
                    v-model="finishForm.finish_reason"
                    rows="3"
                    :state="errors.length ? false : null"
                />
                <b-form-invalid-feedback>{{ $t(errors[0]) }}</b-form-invalid-feedback>
              </ValidationProvider>
            </div>
          </div>
        </b-form>
      </ValidationObserver>
 
      <template #modal-footer>
        <b-button variant="secondary" @click="showFinishModal = false">
          {{ $t('Cancel') }}
        </b-button>
        <b-button
            variant="danger"
            class="button-danger"
            @click="handleFinish()"
            :disabled="finishing"
        >
          <template v-if="finishing">
            <i class="fas fa-spinner fa-spin"></i> {{ $t('Finishing') }}...
          </template>
          <template v-else>
            <i class="fas fa-stop-circle"></i> {{ $t('Finish') }}
          </template>
        </b-button>
      </template>
    </b-modal>
 
    <b-modal
        v-model="showDetailsModal"
        :title="$t('Assignment Details')"
        size="lg"
        centered
        backdrop
        ok-only
        :ok-title="$t('Close')"
        dialog-class="assignment-modal"
    >
      <template #modal-header>
        <div class="d-flex w-100 align-items-center justify-content-between">
          <div class="modal-title-wrapper">
            <h5 class="mb-0">{{ $t('Assignment Details') }}</h5>
          </div>
          <div v-if="selectedAssignment">
            <span :class="getStatusClass(selectedAssignment.status) + ' status-header'">
              {{ getStatusLabel(selectedAssignment.status) }}
            </span>
          </div>
        </div>
      </template>
 
      <div v-if="selectedAssignment" class="assignment-details">
        <div class="row">
          <div class="col-md-6">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Period') }}:</label>
              <div class="detail-value">
                <div>
                  <strong>{{ $t('Start') }}:</strong> {{ formatDate(selectedAssignment.start_date) }}
                </div>
                <div v-if="selectedAssignment.end_date">
                  <strong>{{ $t('End') }}:</strong> {{ formatDate(selectedAssignment.end_date) }}
                </div>
                <div v-else class="text-success">
                  <strong>{{ $t('Active') }}</strong>
                </div>
              </div>
            </div>
          </div>
 
          <div class="col-md-6">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Vehicle Type') }}:</label>
              <div class="detail-value vehicle-type">
                {{ selectedAssignment.vehicle_type ? selectedAssignment.vehicle_type.sigla : '-' }}
                <div v-if="selectedAssignment.vehicle_type" class="small text-muted mt-1">
                  {{ selectedAssignment.vehicle_type.description || selectedAssignment.vehicle_type.name }}
                </div>
              </div>
            </div>
          </div>
 
<!--          <div class="col-md-6">-->
<!--            <div class="detail-group">-->
<!--              <label class="detail-label">{{ $t('Telephone Pulsos') }}:</label>-->
<!--              <div class="detail-value phone-value">{{ formatPhone(selectedAssignment.telephone_pulsos) }}</div>-->
<!--            </div>-->
<!--          </div>-->
 
          <div class="col-md-6">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Center') }}:</label>
              <div class="detail-value">{{ selectedAssignment.center ? selectedAssignment.center.name : '-' }}</div>
            </div>
          </div>
 
          <div class="col-md-6">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Unit') }}:</label>
              <div class="detail-value">{{ selectedAssignment.unit ? selectedAssignment.unit.name : '-' }}</div>
            </div>
          </div>
 
          <div class="col-md-6">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Created By') }}:</label>
              <div class="detail-value">{{ selectedAssignment.created_by ? selectedAssignment.created_by.name : '-' }}</div>
            </div>
          </div>
 
          <div class="col-md-6">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Created At') }}:</label>
              <div class="detail-value">{{ formatDateTime(selectedAssignment.created_at) }}</div>
            </div>
          </div>
 
          <div v-if="selectedAssignment.transfer_reason" class="col-md-12">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Transfer Reason') }}:</label>
              <div class="detail-value">{{ selectedAssignment.transfer_reason }}</div>
            </div>
          </div>
 
          <div v-if="selectedAssignment.finish_reason" class="col-md-12">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Finish Reason') }}:</label>
              <div class="detail-value">{{ selectedAssignment.finish_reason }}</div>
            </div>
          </div>
 
          <div v-if="selectedAssignment.notes" class="col-md-12">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Notes') }}:</label>
              <div class="detail-value">{{ selectedAssignment.notes }}</div>
            </div>
          </div>
 
          <div class="col-md-12" v-if="selectedAssignment.devices && selectedAssignment.devices.length">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Devices') }}:</label>
              <div class="detail-value">
                <div class="device-badges">
                  <span
                      v-for="d in selectedAssignment.devices"
                      :key="d.id"
                      class="badge"
                      :class="isPrimaryDevice(d) ? 'badge-primary' : 'badge-secondary'"
                  >
                    {{ formatDeviceLabel(d) }}<span v-if="isPrimaryDevice(d)"> · {{ $t('Primary') }}</span>
                  </span>
                </div>
                <div v-if="primaryRadiusMeters(selectedAssignment)" class="device-meta-line">
                  {{ $t('Detachment Radius (meters)') }}: <strong>{{ primaryRadiusMeters(selectedAssignment) }}m</strong>
                </div>
                <div
                    v-for="watch in detachedWatches(selectedAssignment)"
                    :key="'watch-' + watch.id"
                    class="device-meta-line device-meta-line--danger"
                >
                  <span class="badge badge-danger mr-1">{{ $t('Detached') }}</span>
                  {{ childDeviceLabel(selectedAssignment, watch.child_device_id) }}: <strong>{{ watch.last_distance_meters }}m</strong>
                </div>
              </div>
            </div>
          </div>
 
          <div v-if="selectedAssignment.updated_at" class="col-md-12">
            <div class="detail-group">
              <label class="detail-label">{{ $t('Last Updated') }}:</label>
              <div class="detail-value">{{ formatDateTime(selectedAssignment.updated_at) }}</div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
 
    <b-modal
        v-model="showConfirmFinishModal"
        :title="$t('Active Assignment Found')"
        size="md"
        centered
        backdrop
        hide-footer
    >
      <div class="py-3">
        <p>
          {{ $t('There is an active assignment for this vehicle. Do you want to finish it before creating a new assignment?') }}
        </p>
        <div class="text-right">
          <b-button variant="secondary" class="mr-2" @click="showConfirmFinishModal = false">
            {{ $t('Cancel') }}
          </b-button>
          <b-button variant="danger" @click="finishActiveAssignment">
            {{ $t('Finish Assignment') }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>
 
<script>
import {ValidationObserver, ValidationProvider} from "vee-validate";
import {BButton, BModal, BForm, BFormInput, BFormTextarea, BFormInvalidFeedback, BFormRadio} from "bootstrap-vue";
import vSelect from "vue-select";
import {mask} from "vue-the-mask";
import axios from "axios";
 
export default {
  name: 'VehicleAssignments',
  components: {
    ValidationObserver,
    ValidationProvider,
    BButton,
    BModal,
    BForm,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BFormRadio,
    vSelect
  },
  directives: {
    mask
  },
  props: {
    vehicleId: {
      type: [String, Number],
      required: true
    }
    ,
    hideHeader: {
      type: Boolean,
      default: false
    }
  },
 
  data() {
    return {
      loading: true,
      showConfirmFinishModal: false,
      assignments: [],
      activeAssignment: null,
      showTransferModal: false,
      showFinishModal: false,
      showDetailsModal: false,
      transferring: false,
      finishing: false,
      finishingId: null,
      transferForm: {
        name: '',
        identifier_code: '',
        vehicle_type_id: '',
        // telephone_pulsos: '',
        center_id: '',
        unit_id: '',
        start_date: '',
        notes: '',
        device_ids: [],
        primary_device_id: null,
        detachment_radius_meters: null,
        // is_carnival: ''
      },
      finishForm: {
        end_date: '',
        finish_reason: '',
        notes: ''
      },
      selectedAssignment: null,
      vehicleTypes: [],
      centers: [],
      transferUnits: [],
      devices: [],
      devicesLoading: false
    };
  },
  computed: {
    /**
     * Ultimo codigo identificador visto neste veiculo — da atribuicao ativa,
     * ou da mais recente ja encerrada. A segunda metade importa: o fluxo de
     * "finalizar e atribuir de novo" apaga a ativa antes de abrir este modal.
     */
    lastIdentifierCode() {
      if (this.activeAssignment && this.activeAssignment.identifier_code) {
        return this.activeAssignment.identifier_code;
      }

      const comCodigo = this.sortedAssignments.find(a => a.identifier_code);

      return comCodigo ? comCodigo.identifier_code : '';
    },

    sortedAssignments() {
      if (!this.assignments) return [];
      return [...this.assignments].sort((a, b) => {
        const aIsActive = a.status === 'active' || !a.end_date;
        const bIsActive = b.status === 'active' || !b.end_date;
 
        if (aIsActive && !bIsActive) return -1;
        if (!aIsActive && bIsActive) return 1;
 
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateB - dateA;
      });
    }
  },
  methods: {
    async loadAssignments() {
      try {
        this.loading = true;
        const response = await axios.get(
            `${this.$store.state.api}vehicles/${this.vehicleId}/assignments`
        );
 
        if (response.data.success) {
          this.assignments = response.data.data.assignments;
          this.activeAssignment = response.data.data.active_assignment;
        }
      } catch (error) {
        this.$error(error);
      } finally {
        this.loading = false;
      }
    },
 
    async loadSelectOptions() {
      try {
        const [vehicleTypesRes, centersRes] = await Promise.all([
          axios.get(`${this.$store.state.api}vehicle_types`),
          axios.get(`${this.$store.state.api}centers`)
        ]);
 
        this.vehicleTypes = vehicleTypesRes.data.data;
        this.centers = centersRes.data.data;
      } catch (error) {
        this.$error(error);
      }
    },
 
    async loadUnitsForCenter(centerId) {
      const clearUnit = arguments.length > 1 ? arguments[1] : true;
      if (!centerId) {
        this.transferUnits = [];
        if (clearUnit) this.transferForm.unit_id = '';
        return;
      }
 
      try {
        const response = await axios.get(
            `${this.$store.state.api}centers/unit/${centerId}`
        );
        this.transferUnits = response.data;
        if (clearUnit) this.transferForm.unit_id = '';
      } catch (error) {
        this.$error(error);
      }
    },
 
    async handleTransfer(validate) {
      if (!validate && this.$refs.transferFormObserver) {
        const isValid = await this.$refs.transferFormObserver.validate();
        if (!isValid) return;
      } else if (validate) {
        const isValid = await validate();
        if (!isValid) return;
      }
 
      try {
        this.transferring = true;
 
        const response = await axios.post(
            `${this.$store.state.api}vehicles/${this.vehicleId}/assignments/transfer`,
            this.transferForm
        );
 
        if (response.data.success) {
          this.$message(
              this.$t("Success"),
              this.$t("Vehicle Assignment successfully"),
              "success"
          );
 
          this.showTransferModal = false;
          await this.loadAssignments();
 
          this.$emit('assignment-changed');
        }
      } catch (error) {
        this.$error(error);
      } finally {
        this.transferring = false;
      }
    },
 
    async finishAssignment(assignment) {
      this.selectedAssignment = assignment;
      this.finishForm.end_date = new Date().toISOString().split('T')[0];
      this.showFinishModal = true;
    },
 
    async handleFinish(validate) {
      if (!validate && this.$refs.finishFormObserver) {
        const isValid = await this.$refs.finishFormObserver.validate();
        if (!isValid) return;
      } else if (validate) {
        const isValid = await validate();
        if (!isValid) return;
      }
 
      try {
        this.finishing = true;
 
        const response = await axios.post(
            `${this.$store.state.api}vehicle_assignments/${this.selectedAssignment.id}/finish`,
            this.finishForm
        );
 
        if (response.data.success) {
          this.$message(
              this.$t("Success"),
              this.$t("Assignment finished successfully"),
              "success"
          );
 
          this.showFinishModal = false;
          await this.loadAssignments();
          await this.updateVehicleStatus(0);
 
          this.$emit('assignment-changed');
        }
      } catch (error) {
        this.$error(error);
      } finally {
        this.finishing = false;
      }
    },
 
    async updateVehicleStatus(status) {
      try {
        await axios.put(`${this.$store.state.api}vehicles/${this.vehicleId}`, { status });
        this.$message(this.$t("Success"), this.$t("Vehicle status updated successfully"), "success");
      } catch (error) {
        this.$error(error);
      }
    },
 
    showDetails(assignment) {
      this.selectedAssignment = assignment;
      this.showDetailsModal = true;
    },
 
    resetTransferForm() {
      this.transferForm = {
        name: '',
        identifier_code: '',
        vehicle_type_id: '',
        // telephone_pulsos: '',
        center_id: '',
        unit_id: '',
        start_date: '',
        notes: '',
        device_ids: [],
        primary_device_id: null,
        detachment_radius_meters: null,
        is_carnival: 0
      };
      this.transferUnits = [];
      this.devices = [];
    },
 
    deviceLabelById(deviceId) {
      const found = (this.devices || []).find(device => Number(device.id) === Number(deviceId));
      return found ? found.display : `#${deviceId}`;
    },
 
    onTransferDeviceIdsChange(deviceIds) {
      // sem pai definido, ou o pai antigo saiu da seleção: o primeiro escolhido vira o pai
      if (!deviceIds.includes(this.transferForm.primary_device_id)) {
        this.transferForm.primary_device_id = deviceIds.length ? deviceIds[0] : null;
      }
      if (deviceIds.length < 2) {
        this.transferForm.detachment_radius_meters = null;
      }
    },
 
    async openTransferModal() {
      try {
        await this.loadSelectOptions();
 
        this.resetTransferForm();

        // O codigo identificador acompanha o prefixo operacional, nao a
        // lataria: numa troca de viatura ele tem que seguir para a atribuicao
        // nova. Deixar em branco romperia o elo com a equipe do despacho em
        // silencio, e a viatura sumiria do mapa.
        this.transferForm.identifier_code = this.lastIdentifierCode || '';
 
        this.showTransferModal = true;
      } catch (e) {
        this.$error(e);
      }
    },
 
    async loadDevicesForTransfer(centerId = null) {
      try {
        this.devicesLoading = true;
        const devicesRes = await axios.get(`${this.$store.state.api}vehicle_assignments/available-devices`, {
          params: {
            vehicle_id: this.vehicleId,
            center_id: centerId || undefined
          }
        });
 
        const rawDevices = devicesRes && devicesRes.data ? (devicesRes.data.data || devicesRes.data) : [];
 
        this.devices = (rawDevices || []).map(d => ({
          id: d.id,
          display: this.buildDeviceOptionLabel(d)
        }));
      } catch (err) {
        this.$error(err);
      } finally {
        this.devicesLoading = false;
      }
    },
 
    async onCenterChange(centerId) {
      this.transferForm.device_ids = [];
      this.devices = [];
      await this.loadUnitsForCenter(centerId);
 
      if (centerId) {
        await this.loadDevicesForTransfer(centerId);
      }
    },
 
    getDevicesPlaceholder() {
      if (!this.transferForm.center_id) {
        return this.$t('Select center first');
      }
 
      return this.devicesLoading ? this.$t('Loading devices...') : this.$t('Select devices');
    },
 
    formatDate(date) {
      if (!date) return '-';
      return date.split('T')[0].split('-').reverse().join('/');
    },
 
    formatDateTime(datetime) {
      if (!datetime) return '-';
      return new Date(datetime).toLocaleString('pt-BR');
    },
 
    formatPhone(value) {
      if (!value) return '-';
      const digits = String(value).replace(/\D/g, '');
      const cleaned = digits.replace(/^55/, '');
      if (cleaned.length === 11) {
        return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      }
      if (cleaned.length === 10) {
        return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      }
      if (cleaned.length === 9) {
        return cleaned.replace(/(\d{5})(\d{4})/, '$1-$2');
      }
      if (cleaned.length === 8) {
        return cleaned.replace(/(\d{4})(\d{4})/, '$1-$2');
      }
      return value;
    },
 
    isPrimaryDevice(device) {
      if (!device) return false;
      const pivot = device.pivot || device;
      return !!(pivot && (pivot.is_primary === true || pivot.is_primary === 1));
    },
 
    primaryRadiusMeters(assignment) {
      if (!assignment || !assignment.devices) return null;
      const primary = assignment.devices.find(d => this.isPrimaryDevice(d));
      return primary && primary.pivot ? primary.pivot.detachment_radius_meters : null;
    },
 
    detachedWatches(assignment) {
      if (!assignment || !assignment.detachment_watches) return [];
      return assignment.detachment_watches.filter(w => w.state === 'detached');
    },
 
    childDeviceLabel(assignment, deviceId) {
      const device = (assignment.devices || []).find(d => Number(d.id) === Number(deviceId));
      return device ? this.formatDeviceLabel(device) : `#${deviceId}`;
    },
 
    formatDeviceLabel(device) {
      if (!device) return '-';
      const desc = device.description || device.name;
      const identifier = device.imei || device.serial_number;
 
      if (desc && identifier) {
        return `${desc} - ${identifier}`;
      }
      if (desc) return desc;
      if (identifier) return identifier;
      return device.id || '-';
    },
 
    buildDeviceOptionLabel(device) {
      if (!device) return '-';
 
      const mainLabel = this.formatDeviceLabel(device);
      const centerName = device.center && device.center.name ? device.center.name : null;
 
      return centerName ? `${mainLabel} (${centerName})` : mainLabel;
    },
 
    getStatusClass(status) {
      const classes = {
        'active': 'badge badge-success',
        'finished': 'badge badge-secondary'
      };
      return classes[status] || 'badge badge-light';
    },
 
    getStatusLabel(status) {
      const labels = {
        'active': this.$t('Active'),
        'finished': this.$t('Finished')
      };
      return labels[status] || status;
    },
 
    handleAssignClick() {
      if (this.activeAssignment && this.activeAssignment.status === 'active') {
        this.showConfirmFinishModal = true;
        return;
      }
 
      this.openTransferModal();
    },
 
    finishActiveAssignment() {
      this.showConfirmFinishModal = false;
      if (this.activeAssignment) {
        this.finishAssignment(this.activeAssignment);
      }
    },
  },
 
  async mounted() {
    await Promise.all([
      this.loadAssignments(),
      this.loadSelectOptions()
    ]);
  },
 
  watch: {
    vehicleId: {
      handler() {
        if (this.vehicleId) {
        this.loadAssignments();
        }
      },
      immediate: true
    }
  }
};
</script>
 
<style scoped>
.vehicle-assignments {
  margin-top: 20px;
}
 
/* Make table wrap and avoid awkward horizontal scrolling where possible */
.vehicle-assignments .table {
  table-layout: fixed;
  width: 100%;
}
.vehicle-assignments .table th,
.vehicle-assignments .table td {
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: anywhere;
}
 
/* Desktop: constrain columns and use ellipsis for overflow to avoid horizontal scrolling */
.vehicle-assignments .table th.col-name,
.vehicle-assignments .table td.col-name {
  width: 18%;
  min-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-period,
.vehicle-assignments .table td.col-period {
  width: 18%;
  min-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-type,
.vehicle-assignments .table td.col-type {
  width: 8%;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-center,
.vehicle-assignments .table td.col-center {
  width: 12%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-unit,
.vehicle-assignments .table td.col-unit {
  width: 10%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-devices,
.vehicle-assignments .table td.col-devices {
  width: 18%;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-status,
.vehicle-assignments .table td.col-status {
  width: 6%;
  white-space: nowrap;
}
.vehicle-assignments .table th.col-created,
.vehicle-assignments .table td.col-created {
  width: 6%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.vehicle-assignments .table th.col-actions,
.vehicle-assignments .table td.col-actions {
  width: 4%;
  white-space: nowrap;
  text-align: center;
}
.required {
  color: red;
  margin-right: 3px;
}
 
.table th {
  border-top: none;
  font-weight: 600;
  font-size: 0.875rem;
}
 
.badge {
  font-size: 0.75rem;
}
 
.btn-group .btn {
  margin-right: 5px;
}
 
.btn-group .btn:last-child {
  margin-right: 0;
}
 
.assignment-details .detail-group {
  margin-bottom: 1.5rem;
}
 
.assignment-details .detail-label {
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: block;
}
 
.assignment-details .detail-value {
  font-size: 0.9rem;
  color: #6c757d;
  line-height: 1.4;
}
 
.assignment-details .badge {
  font-size: 0.8rem;
}
 
.device-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
 
.assignment-modal .device-badges .badge,
.assignment-details .device-badges .badge {
  font-size: 0.85rem;
  padding: 0.4rem 0.7rem;
}
 
.device-meta-line {
  margin-top: 0.6rem;
  font-size: 0.9rem;
  color: #495057;
}
 
.device-meta-line--danger {
  color: #dc3545;
  font-weight: 500;
}
 
.assignment-modal .modal-content {
  background-color: #ffffff;
  color: #645c76;
  border-radius: 0.5rem;
}
 
.assignment-modal .modal-header {
  border-bottom: none;
  padding: 0;
  background-color: transparent;
  border-radius: 0.5rem 0.5rem 0 0;
}
 
.assignment-modal .modal-title {
  font-weight: 600;
  color: #645c76;
  font-size: 1.25rem;
}
 
.assignment-modal .modal-body {
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}
 
.assignment-modal .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
  background-color: transparent;
  color: #645c76;
  border-radius: 0 0 0.5rem 0.5rem;
}
 
.assignment-modal .detail-group {
  margin-bottom: 1.5rem;
}
 
.assignment-modal .detail-label {
  font-weight: 500;
  color: #645c76;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}
 
.assignment-modal .detail-value {
  font-size: 0.9rem;
  color: #645c76;
  line-height: 1.4;
}
 
.assignment-modal .status-header {
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.25rem 0.6rem;
  border-radius: 0.25rem;
  margin-left: 1rem;
}
 
.assignment-modal .card-header {
  padding: 0.75rem 1.25rem;
  border-radius: 0.5rem 0.5rem 0 0;
  color: #645c76;
}
 
.assignment-modal .status-header.badge {
  padding: 0.35rem 0.65rem;
}
 
@media (max-width: 576px) {
  .assignment-modal .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .assignment-modal .status-header {
    margin-left: 0;
    margin-top: 0.5rem;
    align-self: flex-end;
  }
}
 
.assignment-modal .modal-body::-webkit-scrollbar {
  width: 8px;
}
 
.assignment-modal .modal-body::-webkit-scrollbar-track {
  background: #495057;
  border-radius: 4px;
}
 
.assignment-modal .modal-body::-webkit-scrollbar-thumb {
  background: #adb5bd;
  border-radius: 4px;
}
 
.assignment-modal .modal-body::-webkit-scrollbar-thumb:hover {
  background: #868e96;
}
 
/* Small screens: render card-like stacked assignments with label above value (no truncation) */
@media (max-width: 900px) {
  .vehicle-assignments .table-responsive {
    overflow-x: hidden;
  }
  .vehicle-assignments table,
  .vehicle-assignments thead,
  .vehicle-assignments tbody,
  .vehicle-assignments th,
  .vehicle-assignments td,
  .vehicle-assignments tr {
    display: block;
    width: 100%;
  }
  .vehicle-assignments thead { display: none; }
  .vehicle-assignments tr {
    margin-bottom: 12px;
    border: 1px solid #f1f1f1;
    border-radius: 6px;
    padding: 12px;
    background: #fff;
  }
  .vehicle-assignments td {
    display: block;
    padding: 6px 0;
    border-top: 1px solid #f5f5f5;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
  }
  .vehicle-assignments td:first-of-type { border-top: none; }
  .vehicle-assignments td:before {
    content: attr(data-label);
    display: block;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 6px;
  }
  .vehicle-assignments td > * { display: block; }
}
</style>
 
<style>
.modal-dialog {
  max-width: 900px !important;
  margin: 1.75rem auto !important;
}
 
.modal-lg {
  max-width: 900px !important;
}
 
.modal-content {
  border-radius: 8px !important;
  border: none !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
}
 
.modal-header {
  padding: 1.25rem 1.5rem !important;
  border-radius: 8px 8px 0 0 !important;
}
 
.modal-title {
  font-weight: 600 !important;
  color: #645c76 !important;
  font-size: 1.1rem !important;
}
 
.modal-body {
  padding: 1.5rem !important;
  max-height: 70vh !important;
  overflow-y: auto !important;
}
 
.modal-footer {
  padding: 1rem 1.5rem !important;
  border-radius: 0 0 8px 8px !important;
}
 
.modal-body .form-group {
  margin-bottom: 1.25rem !important;
}
 
.modal-body label {
  font-weight: 500 !important;
  margin-bottom: 0.5rem !important;
  color: #645c76 !important;
  font-size: 0.9rem !important;
}
 
.modal-body .form-control,
.modal-body .vselect {
  font-size: 0.9rem !important;
}
 
.modal-body textarea.form-control {
  min-height: 80px !important;
  resize: vertical !important;
}
 
.modal-body .row {
  margin-left: -10px !important;
  margin-right: -10px !important;
}
 
.modal-body .row > [class*="col-"] {
  padding-left: 10px !important;
  padding-right: 10px !important;
}
 
.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.6) !important;
}
 
.modal-body::-webkit-scrollbar {
  width: 6px;
}
 
.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
 
.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}
 
.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
 
@media (max-width: 992px) {
  .modal-dialog {
    max-width: 95% !important;
    margin: 1rem !important;
  }
}
 
@media (max-width: 768px) {
  .modal-body {
    padding: 1rem !important;
  }
 
  .modal-header,
  .modal-footer {
    padding: 1rem !important;
  }
 
  .modal-body .row > [class*="col-"] {
    margin-bottom: 1rem;
  }
}
 
body.dark-layout .assignment-modal .modal-content,
body.dark-layout .assignment-modal .modal-title,
body.dark-layout .assignment-modal .modal-footer,
body.dark-layout .assignment-modal .detail-label,
body.dark-layout .assignment-modal .detail-value,
body.dark-layout .assignment-modal .device-meta-line,
body.dark-layout .assignment-modal .card-header,
body.dark-layout .assignment-modal .modal-body,
body.dark-layout .assignment-modal .modal-dialog {
  color: #d0d2d6 !important;
}
 
body.dark-layout .assignment-modal .status-header.badge {
  color: #fff !important;
}
 
body.dark-layout .assignment-modal .device-meta-line--danger {
  color: #ff6b6b !important;
}
</style>